import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, RotateCw } from 'lucide-react';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import { fmtDateTime, num } from '@/utils/format';

interface Movement {
  id: number;
  product_name?: string;
  variant_label?: string;
  branch_name?: string;
  type: 'in' | 'out' | 'adjust' | string;
  quantity: number;
  reference_type?: string;
  reference_id?: number;
  notes?: string;
  created_at: string;
}

const TONE: Record<string, 'success' | 'danger' | 'warn' | 'neutral'> = {
  in: 'success',
  out: 'danger',
  adjust: 'warn',
};

export function StockMovementsPage() {
  const query = useQuery({
    queryKey: ['stock', 'movements'],
    queryFn: () =>
      apiGet<{ items: Movement[] } | Movement[]>(endpoints.stockMovements),
  });

  const rows = Array.isArray(query.data)
    ? query.data
    : query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Stock movements"
        description="Every in / out / adjustment, in the order it happened."
      />
      {query.isError ? (
        <ErrorState
          title="Couldn't load movements"
          onRetry={() => query.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          loading={query.isLoading}
          rows={rows}
          empty="No stock movements yet."
          columns={[
            {
              key: 'when',
              header: 'When',
              cell: (r) => (
                <span className="text-ink-soft text-xs">
                  {fmtDateTime(r.created_at)}
                </span>
              ),
            },
            {
              key: 'product',
              header: 'Product',
              cell: (r) => (
                <div>
                  <div className="font-medium">
                    {r.product_name ?? '—'}
                  </div>
                  {r.variant_label && (
                    <div className="text-xs text-ink-soft">{r.variant_label}</div>
                  )}
                </div>
              ),
            },
            {
              key: 'branch',
              header: 'Branch',
              hideOnMobile: true,
              cell: (r) => r.branch_name ?? '—',
            },
            {
              key: 'type',
              header: 'Type',
              cell: (r) => (
                <Pill tone={TONE[r.type] ?? 'neutral'}>
                  {r.type === 'in' && <ArrowDownRight className="h-3 w-3" />}
                  {r.type === 'out' && <ArrowUpRight className="h-3 w-3" />}
                  {r.type === 'adjust' && <RotateCw className="h-3 w-3" />}
                  {r.type}
                </Pill>
              ),
            },
            {
              key: 'qty',
              header: 'Qty',
              className: 'text-right',
              cell: (r) => (
                <span
                  className={
                    'tabular-nums font-semibold ' +
                    (r.type === 'in'
                      ? 'text-success'
                      : r.type === 'out'
                        ? 'text-danger'
                        : '')
                  }
                >
                  {r.type === 'out' ? '−' : r.type === 'in' ? '+' : ''}
                  {num(Math.abs(Number(r.quantity)))}
                </span>
              ),
            },
            {
              key: 'ref',
              header: 'Reference',
              hideOnMobile: true,
              cell: (r) =>
                r.reference_type ? (
                  <span className="text-xs text-ink-soft">
                    {r.reference_type}
                    {r.reference_id ? ` #${r.reference_id}` : ''}
                  </span>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      )}
    </>
  );
}
